<template>
  <AreaPageContainer
    v-if="area"
  >
      <!-- Content wrapper with header and footer -->
      <AreaPageBody>
        <AreaHeader />

        <!-- Inner Content -->
        <div class="tw-p-10">
          <!-- Title -->
          <div>
            <h2 class="tw-font-georgia tw-font-bold tw-text-5xl">
              Returns
            </h2>
            <AdvisorCoreStripes />
          </div>

          <!-- Text -->
          <p class="tw-font-poppins tw-mt-10">
            {{ area.headerText }}
          </p>

          <!-- Chart -->
          <div class="tw-mt-10">
            <div :class="{'tw-flex': true, 'tw-flex-col': !area.showChartsAsRow}">
              <div
                class="tw-grow"
              >
                <div>
                  <HighChartsWrapper
                    :name="area.name"
                    :chart-options="activeChartData"
                    :chart-type="activeChartType"
                    export-image
                    @image-changed="(data) => checkupStore.updateSlideImage(data, area.name)"
                    @image-removed="(data) => checkupStore.removeSlideImage(data)"
                    @x-range-changed="updateScalingFactor"
                    v-on="highChartsWrapperEvents"
                  />
                <!-- ChartOptionsSidebar -->
                </div>
              </div>
            </div>

            <table class="tw-mt-10 tw-w-full tw-border-collapse tw-border tw-border-gray-200">
              <thead>
                <tr class="tw-bg-gray-100">
                  <th class="tw-border tw-border-gray-200 tw-py-2 tw-px-4">
                    Trailing Returns by Period
                  </th>
                  <th
                    v-for="series in barChartArea.chartData[0].series"
                    :key="series.name"
                    class="tw-border tw-border-gray-200 tw-py-2 tw-px-4"
                  >
                    {{ series.name }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(category, rowIndex) in barChartArea.chartData[0].xAxis.categories"
                  :key="category"
                >
                  <td class="tw-border tw-border-gray-200 tw-py-2 tw-px-4">
                    {{ category }}
                  </td>
                  <td
                    v-for="series in barChartArea.chartData[0].series"
                    :key="series.name"
                    class="tw-border tw-border-gray-200 tw-py-2 tw-px-4"
                  >
                    <!-- Assuming these values are already in percentage form (e.g., 8.15 means 8.15%) -->
                    {{ series.data[rowIndex] === 0 ? '-' : series.data[rowIndex].toFixed(0) + '%' }}
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- ChartDetailModal will be replaced by ChartClickthroughs -->
            <ChartClickthroughs
              :click-data="chartClickData"
              :get-table-data="area.clickthroughFunction"
            />
          </div>

          <!-- Disclaimer and Methodology -->
          <div class="tw-flex tw-gap-10">
            <!-- <CheckupDisclaimer class="tw-basis-0 tw-grow tw-bg-white !tw-text-[#353a46] tw-px-0" /> -->

            <div class="tw-flex tw-flex-col tw-basis-0 tw-grow tw-py-10 tw-bg-white !tw-text-[#353a46]">
              <h3 class="tw-text-xl tw-font-bold tw-font-georgia">
                Methodology
              </h3>
              <CheckupMethodologyWrapper
                :component-name="area.methodology"
                display-type="short"
                class="tw-text-xs tw-font-poppins"
              />
              <CheckupMethodologyWrapper
                :component-name="area.methodology"
                display-type="long"
                class="tw-text-xs tw-font-poppins tw-hidden"
              />
            </div>
          </div>
        </div>

        <AreaFooter />
        <CheckupDisclaimer class="tw-mt-16">
          <CheckupMethodologyWrapper
            :component-name="area.methodology"
            display-type="long"
          />
        </CheckupDisclaimer>
      </AreaPageBody>

      <!-- Settings Sidebar -->
      <AreaSidebar
        :area="area"
      >
        <IncludeInPresentationToggle :name="area?.name" />
        <template #other>
          <SpeakersNotes :notes="area?.explanation" />
        </template>
        <ChartToggle
          :options="[{value: 'line', label: 'Line', icon: 'fas fa-chart-bar'}, {value: 'bar', label: 'Bar', icon: 'fas fa-th-large'}]"
          @change="updateChartType"
        />
        <!-- <AIChat /> -->
      </AreaSidebar>
  </AreaPageContainer>

  <div v-else>
    <p>Loading...</p>
  </div>
</template>


<script>
import { GRADE_COLORS, getExplanation } from '@frontend/ui/features/checkup/helpers/checkupFunctions.js';
import { useChatStore } from '@frontend/ui/stores/chatStore';
import { useCheckupStore } from '@frontend/ui/stores/checkupStore';
import { mapStores } from 'pinia';

import AreaPageBody from '@frontend/ui/features/checkup/components/analytics/shared/AreaPageBody.vue';
import AreaPageContainer from '@frontend/ui/features/checkup/components/analytics/shared/AreaPageContainer.vue';
import ChartWrapper from '@frontend/ui/components/charts/ChartWrapper.vue';
import ChartClickthroughs from '@frontend/ui/features/checkup/components/ChartClickthroughs.vue';
import CheckupDisclaimer from '@frontend/ui/features/checkup/components/CheckupDisclaimer.vue';
import ReturnsMethodology from '@frontend/ui/features/checkup/components/methodologies/ReturnsMethodology.vue';

import HighChartsWrapper from '@frontend/ui/components/charts/HighChartsWrapper.vue';
import AreaSidebar from '@frontend/ui/features/checkup/components/sidebar/AreaSidebar.vue';
import AdvisorCoreStripes from '@frontend/ui/features/checkup/components/AdvisorCoreStripes.vue';
import IncludeInPresentationToggle from '@frontend/ui/features/checkup/components/sidebar/IncludeInPresentationToggle.vue';
import SpeakersNotes from '@frontend/ui/features/checkup/components/sidebar/SpeakersNotes.vue';
import ChartToggle from '@frontend/ui/features/checkup/components/sidebar/ChartToggle.vue';
import AIChat from '@frontend/ui/features/checkup/components/sidebar/AIChat.vue';
import AreaHeader from '@frontend/ui/features/checkup/components/analytics/shared/AreaHeader.vue';
import CheckupMethodologyWrapper from '@frontend/ui/features/checkup/components/methodologies/CheckupMethodologyWrapper.vue';
import AreaFooter from '@frontend/ui/features/checkup/components/analytics/shared/AreaFooter.vue';
import * as Sentry from '@sentry/vue';

export default {
  name: 'MarketCapArea',
  components: {
    AreaPageContainer,
    AreaPageBody,
    ChartWrapper,
    ChartClickthroughs,
    CheckupDisclaimer,
    ReturnsMethodology,
    HighChartsWrapper,
    AreaSidebar,
    AdvisorCoreStripes,
    IncludeInPresentationToggle,
    SpeakersNotes,
    ChartToggle,
    AIChat,
    AreaHeader,
    CheckupMethodologyWrapper,
    AreaFooter,

  },
  props: {
    isActiveTab: Boolean,
  },
  data() {
    return {
      expanded: false,
      GRADE_COLORS,
      chartClickData: null,
      activeChartType: 'line',
      xStart: 0,
      explanationUpdatePending: false,
      explanationUpdateCompleted: false,
    };
  },
  computed: {
    ...mapStores(useChatStore, useCheckupStore),

    area() {
      return this.checkupStore.areasByName?.['Returns'];
    },
    barChartArea() {
      return this.checkupStore.areasByName?.['Trailing Returns'];
    },
    benchmarkName() {
      const name = this.checkupStore.benchmark?.name;
      return name || 'Benchmark';
    },
    showLineChart() {
      return this.advisorStore.featureFlags.includes('Returns Line Chart');
    },
    lineChartData() {
      return this.area?.chartData?.[0];
    },
    referenceValues() {
      const values = {};

      // Find the data point with the time value closest to xStart
      this.lineChartData?.series?.forEach(series => {
        for (let i = 0; i < series.data.length; i++) {
          const [time, value] = series.data[i];

          if (time > this.xStart) {
            // Previous data point
            const prev = series.data[i ? i - 1 : 0];

            // If the time value of the previous data point is closer, use that one instead.
            if (Math.abs(prev[0] - this.xStart) < time - this.xStart) {
              values[series.name] = prev[1];
            } else {
              // Otherwise, use the current data point.
              values[series.name] = value;
            }
            break;
          }
        }
      });
      return values;
    },
    scalingFactors() {
      const factors = {};
      Object.keys(this.referenceValues).forEach(key => {
        factors[key] = 1 / this.referenceValues[key];
      });
      return factors;
    },
    normalizedChartData() {
      // Make a deep copy of the series data so we don't modify the original data
      const seriesCopy = JSON.parse(JSON.stringify(this.lineChartData.series));

      // Normalize the benchmark y values by the scaling factor
      seriesCopy.forEach(series => {
        const scalingFactor = this.scalingFactors[series.name];
        series.data.forEach(point => {
          // In addition to normalizing to the starting value, we also convert to a percentage that starts at 0%.
          point[1] = (point[1] * (scalingFactor * 100)) - 100;
        });
      });

      return {
        ...this.lineChartData,
        series: seriesCopy,
      };
    },
    activeChartData() {
      console.log('ACTIVE CHART TYPE', this.activeChartType);
      console.log('BAR CHART AREA', this.barChartArea);
      if (this.activeChartType === 'line') {
        const allZeros = this.normalizedChartData.series.every(series =>
          series.data.every(point => !point[1]),
        );

        console.log('lineChartData', this.lineChartData);
        console.log('scalingFactors', this.scalingFactors);
        console.log('normalizedChartData', this.normalizedChartData);

        if (allZeros) {
          console.log('lineChartData', this.lineChartData);
          console.log('scalingFactors', this.scalingFactors);
          console.log('normalizedChartData', this.normalizedChartData);

          Sentry.captureMessage('Line chart data series is all zeros', {
            level: 'warning',
            extra: {
              component: 'NewReturnsArea',
              chartData: this.normalizedChartData,
            },
          });
        }
      }
      if (this.activeChartType === 'bar') return this.barChartArea.chartData[0];
      return this.normalizedChartData;
    },
    highChartsWrapperEvents() {
      const events = {
        'image-changed': data => this.checkupStore.updateSlideImage(data, this.area.name),
        'x-range-changed': this.updateScalingFactor,
      };
      if (this.activeChartType === 'bar') {
        events['seriesPointClick'] = this.handleSeriesPointClick;
      }
      return events;
    },
  },

  watch: {
    // If you are having weird bugs where the active tab seems to be wrong or off by one, double check:
    // :is-active-tab="(i+3) === activeTab" in AutoPortfolioView.vue (the number 3 may be different)
    // 😭
    async isActiveTab() {
      if (this.isActiveTab) {
        const { name, current, proposal, grade } = this.area;
        // await this.chatStore.setContext(mockJSON);

        await this.chatStore.setContext({
          jsonData: {
            currentArea: {
              name,
              [this.checkupStore.portfolio.ame]: current,
              [this.benchmarkName]: proposal,
              magnitudeOfDifference: grade,

              // Dear future developer, it would probably be better to have a chatContextData coming from the area object (for all areas).
              // That way it can be easily customized.
              // This is a band-aid to fix the chat for the Holdings Concentration tab.
              holdingsConcentrationBreakdown: name === 'Concentration Breakdown' ? this.checkupStore.portfolio.sector_market_cap_asset_class_breakdown?.['concentration_breakdown'] : undefined,
            },
            // TODO: Sort highest returns to lowest so we are giving the most important info to openai
            portfolio: {
              name: this.checkupStore.portfolio.name,
              holdings: this.checkupStore.portfolio.holdings.map(h => this.createContextFromHolding(h)),
            },
            benchmark: {
              name: this.benchmarkName,
              holdings: this.checkupStore.benchmark.holdings.map(h => this.createContextFromHolding(h)),
            },
          },
        });
      }
    },
    activeChartData: {
      handler() {
        // this.scheduleExplanationUpdate();
        console.log('Updating explanation for Returns area');
      },
      immediate: true,
    },
  },
  methods: {
    makeValuesPercentages(obj) {
      if (!obj) return {};
      return Object.entries(obj).reduce((acc, [key, value]) => {
        acc[key] = `${((value || 0) * 100).toFixed(2)}%`;
        return acc;
      }, {});
    },
    createContextFromHolding(h) {
      return {
        symbol: h.ticker.symbol,
        dollars: h.dollars,
        returns: this.makeValuesPercentages(h.ticker.trailing_returns_monthly_rebalance),
        sectors: this.makeValuesPercentages(h.ticker.security_breakdown?.['Breakdown']),
        expenseRatio: `${(h.ticker.expense_ratio * 100).toFixed(2)}%`,
        assetClasses: this.makeValuesPercentages(h.ticker.security_breakdown?.['AssetType']),
        marketCap: this.makeValuesPercentages(h.ticker.security_breakdown?.['Market Cap']),
        trailingVolatilities: this.makeValuesPercentages(h.ticker.trailing_volatilities),
      };
    },
    // Maybe make callouts in parallel

    handleChartClick(data) {
      this.chartClickData = data;
    },
    handleSeriesPointClick(pointData) {
      console.log('Series point clicked Returns:', pointData);
      this.chartClickData = pointData;
    },
    updateChartType(chartType) {
      console.log('CHART TYPE', chartType);
      this.activeChartType = chartType;
    },
    updateScalingFactor(event) {
      this.xStart = event.min;
    },
  },
};
</script>


  <style scoped>

  </style>
